import { Component, Input } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ErrorService } from '../../../services/error.service';
import { BehaviorSubject, finalize, Observable } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { DictionaryBaseModel } from '../../../models/dictionary-base-model';

@Component({
  selector: 'app-crud-handler',
  templateUrl: './crud-handler.component.html',
  standalone: true,
  styleUrl: './crud-handler.component.scss'
})
export class CrudHandlerComponent<T extends DictionaryBaseModel> {
  @Input() itemTypeName?: string;
  items!: Array<T>;
  loading = true;
  showItemDialog = false;
  processing$: BehaviorSubject<boolean> = new BehaviorSubject(false);


  protected constructor(
    protected messageService: MessageService,
    protected errorService: ErrorService
  ) { }

  protected handleAdd(addObservable: Observable<T>, itemType: string = 'item') {
    this.processing$.next(true)
    addObservable
      .pipe(
        finalize(() => {
          this.processing$.next(false);
        })
      )
      .subscribe({
      next: (result) => {
        if (result.default) {
          this.items.forEach(i => i.default = i.id === result.id);
        }
        this.items = [...this.items, result];
        this.showAddMessage(itemType);
        this.showItemDialog = false;
      },
      error: (error: HttpErrorResponse) => {
        this.showFailMessage(error, itemType, 'add');
      },
    });
  }

  protected handleEdit(editObservable: Observable<T>, itemType: string = 'item') {
    this.processing$.next(true);
    editObservable
      .pipe(
        finalize(() => {
          this.processing$.next(false);
        })
      )
      .subscribe({
        next: (result) => {
          let itemIndex = this.items.findIndex((i) => i.id === result.id);
          if (typeof itemIndex === 'number') {
            this.items[itemIndex] = result;
          }
          if (result.default) {
            this.items.forEach(i => i.default = i.id === result.id);
          }
          this.items = [...this.items];
          this.showEditMessage(itemType);
          this.showItemDialog = false;
        },
        error: (error: HttpErrorResponse) => {
          this.showFailMessage(error, itemType, 'update');
        },
      });
  }

  protected handleToggle(toggleObservable: Observable<T>, editedItem: T, itemType: string = 'item') {
    toggleObservable.subscribe({
      next: () => {
        this.showToggleMessage(editedItem.enabled, itemType);
      },
      error: (error: HttpErrorResponse) => {
        const item: T | undefined = this.items?.find((i) => i.id === editedItem?.id);
        if (item) {
          item.enabled = !item.enabled;
        }
        this.showFailMessage(error, itemType, 'update');
      },
    });
  }

  protected handleDelete(deleteObservable: Observable<any>, itemId: number, itemType: string = 'item') {
    deleteObservable.subscribe({
      next: () => {
        const itemIndex = this.items?.findIndex((i) => i.id === itemId);
        if (itemIndex >= 0) {
          this.items?.splice(itemIndex, 1);
          this.items = [...this.items];
        }
        this.showDeleteMessage(itemType);
      },
      error: (error: HttpErrorResponse) => {
        this.showFailMessage(error, itemType, 'delete');
      },
    });
  }

  private showAddMessage(itemType: string) {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: `${itemType} added.`,
    });
  }

  private showEditMessage(itemType: string) {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: `${itemType} updated.`,
    });
  }

  private showToggleMessage(enabled: boolean | undefined, itemType: string) {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: `${itemType} ${enabled ? 'enabled' : 'disabled'}.`,
    });
  }

  private showDeleteMessage(itemType: string) {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: `${itemType} deleted.`,
    });
  }

  private showFailMessage(httpErrorResponse: HttpErrorResponse, itemType: string, operationType: 'add' | 'delete' | 'update' | 'upload') {
    this.errorService.handleError(httpErrorResponse,`Could not ${operationType} the ${itemType.toLowerCase()}.`);
  }

}
